//Proofed
import SearchIcon from '@mui/icons-material/Search'
import ListAltIcon from '@mui/icons-material/ListAlt'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import PeopleIcon from '@mui/icons-material/People'
import PublicIcon from '@mui/icons-material/Public'
import BarChartIcon from '@mui/icons-material/BarChart'
import AssessmentIcon from '@mui/icons-material/Assessment'
import HandshakeIcon from '@mui/icons-material/Handshake'
import FilterListIcon from '@mui/icons-material/FilterList'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { CONTRACT_NUM } from '../common/common'
import type { HowItWorksStep } from '../components/HowItWorksSection'
import type { StatItem } from '../components/StatsSection'

export const analyticsHighlights = [
    '8 specialist dashboards covering the full procurement lifecycle',
    'Filter by source, stage, value, duration, location and CPV code',
    'Buyer analysis: pipelines, procurement methods, tender windows and contact details',
    'Supplier intelligence: competitor activity and buyer relationships',
    'Regional and industry trends across UK & Ireland public sector',
]



export const analyticsFeatures2 = [
    'Buyer spend profiling across all public sector bodies',
    'Pipeline forecasting 12-month forward view',
    'Competitor award analysis',
    'Market segmentation by region, category & value',
    'Spending trend analysis',
    'Opportunity scoring & prioritisation',
]


export const analyticsHIWTitle = "How CFP Analytics Works"
export const analyticsHIWSubtitle = "Three steps from market search to data-driven pipeline decisions.";

export const analyticsSteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: FilterListIcon,
        title: 'Search & Filter the Market',
        body: 'Use powerful filters source, stage, value, duration, location and CPV code or enter a natural language keyword query to identify the exact opportunities relevant to your products and services',
    },
    {
        num: '2',
        Icon: ManageSearchIcon,
        title: 'Analyse Buyers & Suppliers',
        body: 'Dive into buyer behaviour, procurement methods, tender windows, supplier relationships and contact details. Research competitors, spot potential partners and understand the market before you bid',
    },
    {
        num: '3',
        Icon: TrendingUpIcon,
        title: 'Plan Your Pipeline',
        body: 'Use regional, industry and opportunity dashboards to map upcoming renewals, spot growth trends in your sector and build a data-driven 12-month forward pipeline',
    },
]

export const analyticsStats: StatItem[] = [
    { value: CONTRACT_NUM, label: 'Contracts in the database' },
    { value: '8', label: 'Specialist analytics dashboards' },
    // { value: '200+', label: 'Portals aggregated' },
    { value: '6000', label: 'Buying organisations tracked' },
    { value: '40%', label: 'of tenders not published on Find a Tender' },
]

export const analyticsWhoUses = [
    { role: 'BD Directors', desc: 'Setting strategy and identifying the highest-growth market segments in UK & Ireland public sector' },
    { role: 'Account Managers', desc: 'Researching buyer history and current supplier relationships before client meetings and renewals' },
    { role: 'Bid Writers', desc: "Understanding the buyer's priorities, award history and procurement methods to write more targeted proposals" },
    { role: 'Sales Leaders', desc: 'Building a data-driven pipeline forecast and tracking progress against real market opportunity' },
];

export const analyticsFeatures = [
    {
        Icon: SearchIcon,
        title: 'Search Dashboard',
        body: 'Powerful filtering by source, stage, value, duration and location. Dive into CPV codes, frameworks and DPS, or use natural language keyword queries to find the exact opportunities relevant to your products and services',
    },
    {
        Icon: ListAltIcon,
        title: 'Opportunities Dashboard',
        body: 'Explore the full procurement lifecycle in one place: planning stage, live tenders, active contracts, contract awards and upcoming renewals. Includes framework and dynamic purchasing system data',
    },
    {
        Icon: ManageSearchIcon,
        title: 'Buyer Dashboard',
        body: 'Understand how any contracting authority goes to market. Analyse buyer pipelines, procurement methods, supplier relationships, tender windows, tender durations and access contact details',
    },
    {
        Icon: PeopleIcon,
        title: 'Supplier Dashboard',
        body: "Competitive intelligence on any supplier's market presence, procurement methods and buyer relationships via contract award history. Includes Companies House data for additional business intelligence",
    },
    {
        Icon: PublicIcon,
        title: 'Region Dashboard',
        body: 'Break down procurement activity by geography. Analyse patterns at country level, local authority level or using ITL/NUTS classifications for the most granular regional insights available',
    },
    {
        Icon: BarChartIcon,
        title: 'Industry Dashboard',
        body: 'Track spending and contract volumes by CPV category. Understand seasonal patterns, identify high-growth segments and see how services, goods and works behave differently across sectors',
    },
    {
        Icon: AssessmentIcon,
        title: 'KPI Evaluation',
        body: 'Assess how public sector organisations perform their procurement processes. Understand effectiveness, cycle times and identify which types of opportunity are most suitable for your business',
    },
    {
        Icon: HandshakeIcon,
        title: 'Social Indicators',
        body: "Understand the social and economic impact of public procurement. Valuable if you're focused on social value scoring or want to understand the broader drivers behind public sector spending decisions",
    },
]


export const analyticsFaqs = [
    {
        q: 'What is CFP Analytics?',
        a: 'CFP Analytics is a suite of 8 specialist data analytics dashboards providing strategic intelligence on the UK & Ireland public sector procurement market. It covers contract search, opportunities, buyer behaviour, supplier activity, regional trends, industry analysis, KPI evaluation and social indicators',
    },
    {
        q: 'How many dashboards are included?',
        a: 'There are 8 specialist dashboards, each focused on a specific type of market intelligence: Search, Opportunities, Buyers, Suppliers, Regions, Industry, KPI Evaluation and Social Indicators',
    },
    {
        q: 'Can I research a specific buyer before bidding?',
        a: "Yes. The Buyer Dashboard lets you analyse any contracting authority's procurement behaviour, including their pipelines, procurement methods, supplier relationships, tender windows, durations and contact details everything you need to tailor your bid to their priorities",
    },
    {
        q: 'Can I track competitor activity?',
        a: "Yes. The Supplier Dashboard shows any supplier's market presence, procurement methods, buyer relationships via contract award history and includes Companies House data for additional context on your competitors or potential partners",
    },
    {
        q: 'How do I find tenders relevant to my business?',
        a: "Use the Search Dashboard's advanced filters source, stage, value, duration, location, CPV code or enter a natural language keyword query describing your products and services. The platform surfaces the opportunities most relevant to what you do",
    },
    {
        q: 'Does CFP Analytics cover Frameworks and DPS?',
        a: 'Yes. Both the Search and Opportunities dashboards include framework and dynamic purchasing system data, so you can identify framework opportunities and analyse activity within specific DPS arrangements',
    },
    // {
    //     q: 'Can I trial CFP Analytics before committing?',
    //     a: "Yes you get a full 14-day free trial with access to every feature. A credit card is required to activate the trial but you won't be charged until the trial period ends. Cancel anytime, no questions asked.",
    // },
]

